import { forwardRef, useImperativeHandle, useState, useEffect, useRef, useMemo, useLayoutEffect } from 'react';
import { Splide, SplideSlide } from '@splidejs/react-splide';
import InstaModalDrawer from '@/components/InstaModalDrawer';
import MobxWrap from '@/components/MobxWrap';
import { ClassNames } from '@/utils';
import { ProductTracker } from '@/utils/Tracker';
import styles from './index.module.less';
import { isEmpty } from 'lodash';
import ProductMasterDataConfig from '@/pages/product/components/ProductMaster/data.config';

const closeIcon =
  'https://res.insta360.com/static/5074b390a75025c24446d7bd62ccf014/close_FILL0_wght400_GRAD0_opsz48%20(1)%201.png';

const CommodityModal = MobxWrap(
  ['product', 'app', 'area', 'abtest'],
  forwardRef((props, ref) => {
    const {
      app: { abTestData },
      product: { carePlusPrice, productDetail },
      area: { country },
      abtest: { isTargetingResult },
    } = props;
    const MAX_MASTER_HEIGHT_REF = useRef(0);
    const [visible, setVisible] = useState(false);
    const [commodity, setCommodity] = useState({});
    const [commodityMasterHeihgt, setCommodityMasterHeight] = useState(MAX_MASTER_HEIGHT_REF.current);
    /** 当前实验组数据 */
    const curTestData = abTestData.find?.((data) => data.flagName === 'product_page_bundle_select');
    const showGO3SProductScene = useMemo(() => {
      if (ProductMasterDataConfig.GO3SProductSceneCommodities.has(commodity.id) && country.X_Language !== 'zh_cn')
        return true;

      return false;
    }, [commodity, country]);

    // 获取设备屏幕宽度
    useEffect(() => {
      if (window) {
        const clientWidth = window.innerWidth;
        setCommodityMasterHeight(clientWidth);
        MAX_MASTER_HEIGHT_REF.current = clientWidth;
      }
    }, []);

    const onShow = (commodity) => {
      setVisible(true);
      setCommodity(commodity);
      setCommodityMasterHeight(MAX_MASTER_HEIGHT_REF.current);
      ProductTracker.bundlePopup({
        action: 'expose_bundle_popup',
        label: commodity?.id,
        value: curTestData?.targetingResult,
      });
    };

    const onClose = () => {
      // setStateValue('curSelectCommodityId', commodity.id);
      // setStateValue('isSelectPersonalized', false);
      // onChangeCommodity(commodity.id);
      setVisible(false);
      ProductTracker.bundlePopup({
        action: 'close_bundle_popup',
        label: commodity?.id,
        value: curTestData?.targetingResult,
      });
    };

    const cinfos = useMemo(() => {
      if (!commodity.info?.introduction) return '';
      const introduction = commodity.info?.introduction;
      const cid = commodity.id;
      // 设置carePlus简介中的价格变量
      if (!isEmpty(carePlusPrice?.get(cid))) {
        return introduction.replace('__PRICE__', carePlusPrice?.get(cid) ?? '');
      } else return introduction;
    }, [commodity.info?.introduction, carePlusPrice, commodity.id]);

    useImperativeHandle(ref, () => {
      return {
        onShow,
      };
    });

    const isF2PExperiment =
      isTargetingResult('F2P-SP-image-test', 'Original') && productDetail?.id === 32 && commodity.displays?.length >= 9;

    if (isF2PExperiment) commodity.displays.splice(1, 8);

    return (
      <InstaModalDrawer destroyOnClose open={visible} closable={false} className={styles.wrap} onCancel={onClose}>
        <div className={styles.commodityModal}>
          <CommodityMaster
            commodity={commodity}
            displays={commodity.displays}
            height={commodityMasterHeihgt}
            onClose={onClose}
            planName={curTestData?.targetingResult}
            showGO3SProductScene={showGO3SProductScene}
          />
          <div className={styles.contentWrap}>
            <div className={styles.commodityName}>{commodity.commodityName}</div>
            <div className={styles.introWrap} dangerouslySetInnerHTML={{ __html: cinfos }} />
          </div>
        </div>
      </InstaModalDrawer>
    );
  })
);

const CommodityMaster = (props) => {
  const { displays = [], commodity, height, onClose, planName, showGO3SProductScene = false } = props;
  const [activeIndex, setActiveIndex] = useState(0);
  const splideRef = useRef(null);
  const MIN_HEIGHT = 188;

  // 套餐选中更新时初始化
  useLayoutEffect(() => {
    if (showGO3SProductScene && displays.length > 1) {
      splideRef.current.splide.go(1);
      setActiveIndex(1);
      return;
    }
    splideRef.current.splide.go(0);
    setActiveIndex(0);
  }, [displays, showGO3SProductScene]);

  useEffect(() => {
    ProductTracker.bundlePopup({
      action: 'expose_image',
      label: `${commodity?.id}_${activeIndex + 1}`,
      value: planName,
    });
  }, [activeIndex]);

  const handleMove = (splide, newIndex) => {
    setActiveIndex(newIndex);
  };

  return (
    <section className={ClassNames([styles.commodityMaster, height < MIN_HEIGHT ? styles.shadow : ''])}>
      <div className={styles.sliderWrap}>
        <Splide
          ref={(ref) => (splideRef.current = ref)}
          options={{
            arrows: false,
            pagination: false,
            type: 'loop',
            lazyLoad: 'nearby',
            heightRatio: 1,
            drag: displays?.length > 1,
            height: `${height}px`,
          }}
          className={styles.sliderContent}
          onMove={handleMove}
        >
          {displays.map((display) => {
            return (
              <SplideSlide className={styles.item} key={display.id}>
                <img className={styles.itemImg} data-splide-lazy={display?.url_m} alt='' />
              </SplideSlide>
            );
          })}
        </Splide>
        <div className={styles.closeIcon} onClick={onClose}>
          <img src={closeIcon} alt='' />
        </div>
        <div className={styles.pagination}>{`${activeIndex + 1}/${displays?.length}`}</div>
      </div>
    </section>
  );
};

export default CommodityModal;
